import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  faAngleDoubleUp,
  faAngleDoubleDown,
  faAngleDoubleLeft,
  faAngleDoubleRight,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cx from 'classnames'
import React from 'react'

import { activateNextPane } from '../../actions/play-actions'
import {
  Pane,
  PaneClasses,
  SHOW_LEADERBOARD_TOOLTIP,
  SHOW_ROUND_DASHBOARD_TOOLTIP,
  SHOW_TRACK_GALLERY_TOOLTIP,
} from '../../constants/constants'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { selectIsFocus } from '../../reducers/focusSlice'
import modalsSlice from '../../reducers/modalsSlice'
import sessionSlice from '../../reducers/sessionSlice'
import { selectOwnedMatchSlug, selectTrackScore } from '../../selectors/blaster-peer-selectors'
import {
  selectCurrentActiveGamers,
  selectCurrentPlaylistInfo,
  selectCurrentTrackInfo,
} from '../../selectors/current-play-selectors'
import { selectMatchTitle } from '../../selectors/match-selectors'
import {
  selectCurrentMatchSlug,
  selectCurrentTrackImageUrl,
  selectHasSpotifyToken,
  selectIsLandscape,
  selectIsShowSettings,
  selectIsShowSocial,
  selectMode,
} from '../../selectors/session-selectors'
import { PlayActions, SettingsActions } from '../../types'
import { getSpotifyUriFromTrackInfo, isMobile as getIsMobile } from '../../util/track-utils'
// import SpotifyPlayback from '../widgets/SpotifyPlayback'
import GameSettings from './GameSettings'
import PlayControls from './PlayControls'
import ScoreLanes from './ScoreLanes'
import { difficultyString } from '../../util/score-utils'

type Props = {
  settingsActions: SettingsActions
  playActions: PlayActions
}

const HeaderContainer = ({ settingsActions, playActions }: Props) => {
  const isMobile = getIsMobile()
  const dispatch = useAppDispatch()
  const isLandscape = useAppSelector(selectIsLandscape)
  const trackInfo = useAppSelector(selectCurrentTrackInfo)
  const isShowSocial = useAppSelector(selectIsShowSocial)
  const isShowSettings = useAppSelector(selectIsShowSettings)
  const hasSpotifyToken = useAppSelector(selectHasSpotifyToken)
  const isCurrFocus = useAppSelector(selectIsFocus(Pane.SETTINGS))
  const spotifyUri = getSpotifyUriFromTrackInfo(trackInfo)
  const isUseSpotifyImage = hasSpotifyToken && spotifyUri
  const trackImageUrl = useAppSelector(selectCurrentTrackImageUrl)
  const {
    slug: trackSlug,
    title: trackTitle = '[no track]',
    artist: trackArtist,
    wordCount,
  } = trackInfo
  const { wordDensity } = useAppSelector(selectTrackScore(trackInfo))
  const mode = useAppSelector(selectMode)
  const ownedMatchSlug = useAppSelector(selectOwnedMatchSlug)
  const currentMatchSlug = useAppSelector(selectCurrentMatchSlug)
  const [matchOwner, matchSlug] = currentMatchSlug.split('/')
  const isOwnedMatch = ownedMatchSlug === currentMatchSlug
  const isBlasting = mode === 'play' && !isOwnedMatch
  const matchTitle = useAppSelector(selectMatchTitle(matchOwner, matchSlug))
  const { title: currentSetTitle } = useAppSelector(selectCurrentPlaylistInfo)
  const activeGamers = useAppSelector(selectCurrentActiveGamers)

  const onToggleSocial = () => {
    dispatch(sessionSlice.actions.setIsShowSocial(!isShowSocial))
  }
  const onToggleSettings = () => {
    dispatch(sessionSlice.actions.setIsShowSettings(!isShowSettings))
  }
  const trackTitleClass = cx('currTrackTitle', { disabled: !trackSlug })
  const className = cx(PaneClasses[Pane.SETTINGS], {
    hasFocus: isCurrFocus,
    mobile: isMobile,
    single: activeGamers.length === 1,
  })
  const onClick = () => {
    // console.log(`container: ${PaneClasses[Pane.SETTINGS]}`)
    dispatch(activateNextPane({ pane: Pane.SETTINGS }))
  }
  const centerClassname = cx('center', { withPlayer: isBlasting })
  const getTrackBadge = () => {
    return (
      <div className="trackBadge">
        {isUseSpotifyImage && (
          <div className="spotifyInfo">
            <img src={trackImageUrl} alt="" />
          </div>
        )}
        <div className={trackTitleClass}>
          <button onClick={openTrackGallery} className="title" title={SHOW_TRACK_GALLERY_TOOLTIP}>
            {trackTitle}
          </button>
          <span className="artist">{trackArtist}</span>
          <div className="songInfo">
            <div className="title">
              <span className="wordcount">
                Song words:&nbsp;&nbsp;<b>{wordCount}</b>
              </span>
              <span className="pointsperword">
                Difficulty:&nbsp;&nbsp; <b>{difficultyString(wordDensity)}</b>
              </span>
            </div>
          </div>
        </div>
      </div>
    )
  }
  const openLeaderBoard = () => {
    dispatch(modalsSlice.actions.toggleLeaderboardModal({ isOpen: true, initialTab: 0 }))
  }
  const openRoundDashboard = () => {
    dispatch(modalsSlice.actions.toggleLeaderboardModal({ isOpen: true, initialTab: 1 }))
  }
  const openTrackGallery = () => {
    dispatch(modalsSlice.actions.toggleLeaderboardModal({ isOpen: true, initialTab: 2 }))
  }
  const getMatchSection = () => {
    return (
      <div className="playerInfo">
        <div className="title">
          <button className="large" onClick={openLeaderBoard} title={SHOW_LEADERBOARD_TOOLTIP}>
            {matchTitle}
          </button>
          <button
            className="menutrack"
            onClick={openRoundDashboard}
            title={SHOW_ROUND_DASHBOARD_TOOLTIP}
          >
            {currentSetTitle || '--'}
          </button>
        </div>
      </div>
    )
  }
  const getRight = () => {
    if (isMobile && !isLandscape) return null
    return (
      <div className="right">
        {isBlasting && <ScoreLanes />}
        <div className="edge">
          <button className="top" onClick={onToggleSocial}>
            <FontAwesomeIcon
              icon={(isShowSocial ? faAngleDoubleRight : faAngleDoubleLeft) as IconProp}
              title={`${isShowSocial ? 'hide' : 'show'} Match Explorer sidebar`}
              size="sm"
            />
          </button>
          <button className="bottom" onClick={onToggleSettings}>
            <FontAwesomeIcon
              icon={(isShowSettings ? faAngleDoubleUp : faAngleDoubleDown) as IconProp}
              title={`${isShowSettings ? 'hide' : 'show'} Play Settings`}
              size="sm"
            />
          </button>
        </div>
      </div>
    )
  }
  return (
    <div className={className} onClick={onClick}>
      <div className="banner">
        <div className="left">
          <div className="edge" />
          <div className="body">
            {!isMobile && getMatchSection()}
            {getTrackBadge()}
          </div>
        </div>
        <div className={centerClassname}>
          {isMobile && !isLandscape && <PlayControls playActions={playActions} />}
        </div>
        {getRight()}
      </div>
      {isMobile && !isLandscape && <ScoreLanes />}
      {isShowSettings && <GameSettings settingsActions={settingsActions} />}
    </div>
  )
}

export default HeaderContainer
